import { useEffect, useMemo, useRef, useState } from "react";
import { displayValue, formatBackupSize, formatBackupTime } from "@/lib/format";
import { LABEL, trustValue } from "@/lib/trust";
import { Badge } from "@/components/ui/Badge";
import { Drawer } from "@/components/ui/Drawer";
import {
  assembleOperationalState,
  assembleEvidenceCenter,
  backupStatusLabel,
  deriveIntegrations,
  formatDeployTimestamp,
  incidentIdForRemediation,
} from "@aisecure/status";
import {
  EvidenceCenterPanel,
  OperationalTruthPanel,
  RuntimeDoctorStatus,
  RuntimeHealthDomainsPanel,
  VerificationHistoryPanel,
} from "@aisecure/status/ui";
import {
  executeIncidentAction,
  fetchPlatformVersion,
  fetchTenantLogs,
  fetchVerificationHistory,
  getCustomer,
  getDnsStatus,
  getKnowledgeStatus,
  getSecurityHealth,
  getSslStatus,
  listIntegrations,
  listSecrets,
  type CustomerApi,
  type DnsStatusApi,
  type IntegrationStatusApi,
  type KnowledgeStatusApi,
  type PlatformVersionApi,
  type SecretInventoryItem,
  type SecurityHealthApi,
  type SslStatusApi,
  type VerificationHistoryItemApi,
} from "@/lib/api";
import type { AttentionItem, IncidentActionResult, TenantDetail } from "@/types";

interface TenantControlCenterDrawerProps {
  detail: TenantDetail | null;
  onClose: () => void;
  /** Attention/incident rows for this tenant — same IDs as Incident Workspace. */
  attentionItems?: AttentionItem[];
  /** Real doctor / operations message when present. */
  doctorMessage?: string | null;
  /** Platform-domain VerificationResult records from /api/platform/operations. */
  platformVerifications?: Array<Record<string, unknown>> | null;
  /** Open shared incident workspace for the primary incident. */
  onOpenIncident?: (incidentId: string) => void;
  /** Execute remediation via existing incident action API; parent refreshes fleet state. */
  onExecuteAction?: (incidentId: string, actionId: string) => Promise<IncidentActionResult>;
  /** After a successful action, refresh verification history / tenant snapshot. */
  onActionComplete?: () => void | Promise<void>;
}

export function TenantControlCenterDrawer({
  detail,
  onClose,
  attentionItems = [],
  doctorMessage = null,
  platformVerifications = null,
  onOpenIncident,
  onExecuteAction,
  onActionComplete,
}: TenantControlCenterDrawerProps) {
  const [customer, setCustomer] = useState<CustomerApi | null>(null);
  const [knowledge, setKnowledge] = useState<KnowledgeStatusApi | null>(null);
  const [integrations, setIntegrations] = useState<IntegrationStatusApi[] | null>(null);
  const [dns, setDns] = useState<DnsStatusApi | null>(null);
  const [ssl, setSsl] = useState<SslStatusApi | null>(null);
  const [secrets, setSecrets] = useState<SecretInventoryItem[] | null>(null);
  const [securityHealth, setSecurityHealth] = useState<SecurityHealthApi | null>(null);
  const [platformVersion, setPlatformVersion] = useState<PlatformVersionApi | null>(null);
  const [customerError, setCustomerError] = useState<string | null>(null);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logLines, setLogLines] = useState<string[] | null>(null);
  const [logsFetchedAt, setLogsFetchedAt] = useState<string | null>(null);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [logsMessage, setLogsMessage] = useState<string | null>(null);
  const [logsAvailable, setLogsAvailable] = useState<boolean | null>(null);
  const [verificationHistory, setVerificationHistory] = useState<VerificationHistoryItemApi[]>([]);
  const [verificationHistoryLoading, setVerificationHistoryLoading] = useState(false);
  const [verificationHistoryError, setVerificationHistoryError] = useState<string | null>(null);
  const [actionBusyKey, setActionBusyKey] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  /** Immediate Backup panel update from backup.run verification.detail before fleet refresh lands. */
  const [backupOverride, setBackupOverride] = useState<{
    lastBackup: string | null;
    lastBackupBytes: number | null;
    lastBackupPath: string | null;
    lastBackupMtime: string | null;
    lastBackupChecksum: string | null;
  } | null>(null);
  const doctorReportRef = useRef<HTMLElement | null>(null);
  const evidenceRef = useRef<HTMLElement | null>(null);

  const loadVerificationHistory = async (slug: string) => {
    setVerificationHistoryLoading(true);
    setVerificationHistoryError(null);
    try {
      const response = await fetchVerificationHistory({ tenant: slug, limit: 100 });
      setVerificationHistory(response.results ?? []);
    } catch (err) {
      setVerificationHistory([]);
      setVerificationHistoryError(
        err instanceof Error ? err.message : "Failed to load verification history",
      );
    } finally {
      setVerificationHistoryLoading(false);
    }
  };

  useEffect(() => {
    setBackupOverride(null);
  }, [detail?.slug]);

  useEffect(() => {
    if (!detail) {
      setCustomer(null);
      setKnowledge(null);
      setIntegrations(null);
      setDns(null);
      setSsl(null);
      setPlatformVersion(null);
      setCustomerError(null);
      setVerificationHistory([]);
      setVerificationHistoryError(null);
      return;
    }
    let cancelled = false;
    setCustomer(null);
    setKnowledge(null);
    setIntegrations(null);
    setDns(null);
    setSsl(null);
    setPlatformVersion(null);
    setCustomerError(null);
    void loadVerificationHistory(detail.slug);

    void getCustomer(detail.slug)
      .then((row) => {
        if (!cancelled) setCustomer(row);
      })
      .catch((err) => {
        if (!cancelled) {
          setCustomerError(err instanceof Error ? err.message : "Customer registry unavailable");
        }
      });

    void getKnowledgeStatus(detail.slug)
      .then((row) => {
        if (!cancelled) setKnowledge(row);
      })
      .catch(() => {
        if (!cancelled) setKnowledge(null);
      });

    void listIntegrations(detail.slug)
      .then((rows) => {
        if (!cancelled) setIntegrations(rows);
      })
      .catch(() => {
        if (!cancelled) setIntegrations(null);
      });

    void getDnsStatus(detail.slug)
      .then((row) => {
        if (!cancelled) setDns(row);
      })
      .catch(() => {
        if (!cancelled) setDns(null);
      });

    void getSslStatus(detail.slug)
      .then((row) => {
        if (!cancelled) setSsl(row);
      })
      .catch(() => {
        if (!cancelled) setSsl(null);
      });

    void listSecrets(detail.slug)
      .then((rows) => {
        if (!cancelled) setSecrets(rows);
      })
      .catch(() => {
        if (!cancelled) setSecrets(null);
      });

    void getSecurityHealth(detail.slug)
      .then((row) => {
        if (!cancelled) setSecurityHealth(row);
      })
      .catch(() => {
        if (!cancelled) setSecurityHealth(null);
      });

    void fetchPlatformVersion()
      .then((row) => {
        if (!cancelled) setPlatformVersion(row);
      })
      .catch(() => {
        if (!cancelled) setPlatformVersion(null);
      });

    return () => {
      cancelled = true;
    };
  }, [detail]);

  const operational = useMemo(() => {
    if (!detail) return null;
    const customerInput = {
      slug: detail.slug,
      status: customer?.status ?? "unknown",
      provision_completed_at: customer?.provision_completed_at ?? null,
      last_health_status: customer?.last_health_status ?? null,
      image_tag: customer?.image_tag ?? null,
    };
    return assembleOperationalState({
      slug: detail.slug,
      displayName: detail.displayName,
      customer: customerInput,
      tenantHealth: {
        health: detail.health,
        doctor_status: detail.doctorStatus,
        platform_version: detail.platformVersion,
        has_stack: detail.hasLiveData,
        last_backup: detail.lastBackup,
        runtime_probes: detail.runtimeProbes
          ? {
              control_plane_backend: detail.runtimeProbes.controlPlaneBackend
                ? {
                    status: detail.runtimeProbes.controlPlaneBackend.status,
                    message: detail.runtimeProbes.controlPlaneBackend.message,
                    check_id: detail.runtimeProbes.controlPlaneBackend.checkId,
                    latency_ms: detail.runtimeProbes.controlPlaneBackend.latencyMs,
                    recommendation: detail.runtimeProbes.controlPlaneBackend.recommendation,
                    verified_at: detail.runtimeProbes.controlPlaneBackend.verifiedAt,
                  }
                : null,
              operations_api: detail.runtimeProbes.operationsApi
                ? {
                    status: detail.runtimeProbes.operationsApi.status,
                    message: detail.runtimeProbes.operationsApi.message,
                    check_id: detail.runtimeProbes.operationsApi.checkId,
                    latency_ms: detail.runtimeProbes.operationsApi.latencyMs,
                    recommendation: detail.runtimeProbes.operationsApi.recommendation,
                    verified_at: detail.runtimeProbes.operationsApi.verifiedAt,
                  }
                : null,
              authentication: detail.runtimeProbes.authentication
                ? {
                    status: detail.runtimeProbes.authentication.status,
                    message: detail.runtimeProbes.authentication.message,
                    check_id: detail.runtimeProbes.authentication.checkId,
                    latency_ms: detail.runtimeProbes.authentication.latencyMs,
                    recommendation: detail.runtimeProbes.authentication.recommendation,
                    verified_at: detail.runtimeProbes.authentication.verifiedAt,
                  }
                : null,
              internal_platform_services: detail.runtimeProbes.internalPlatformServices
                ? {
                    status: detail.runtimeProbes.internalPlatformServices.status,
                    message: detail.runtimeProbes.internalPlatformServices.message,
                    check_id: detail.runtimeProbes.internalPlatformServices.checkId,
                    latency_ms: detail.runtimeProbes.internalPlatformServices.latencyMs,
                    recommendation:
                      detail.runtimeProbes.internalPlatformServices.recommendation,
                    verified_at: detail.runtimeProbes.internalPlatformServices.verifiedAt,
                  }
                : null,
              email_processing: detail.runtimeProbes.emailProcessing
                ? {
                    status: detail.runtimeProbes.emailProcessing.status,
                    message: detail.runtimeProbes.emailProcessing.message,
                    check_id: detail.runtimeProbes.emailProcessing.checkId,
                    latency_ms: detail.runtimeProbes.emailProcessing.latencyMs,
                    recommendation: detail.runtimeProbes.emailProcessing.recommendation,
                    verified_at: detail.runtimeProbes.emailProcessing.verifiedAt,
                  }
                : null,
              worker: detail.runtimeProbes.worker
                ? {
                    status: detail.runtimeProbes.worker.status,
                    message: detail.runtimeProbes.worker.message,
                    check_id: detail.runtimeProbes.worker.checkId,
                    latency_ms: detail.runtimeProbes.worker.latencyMs,
                    recommendation: detail.runtimeProbes.worker.recommendation,
                    verified_at: detail.runtimeProbes.worker.verifiedAt,
                  }
                : null,
              database: detail.runtimeProbes.database
                ? {
                    status: detail.runtimeProbes.database.status,
                    message: detail.runtimeProbes.database.message,
                    check_id: detail.runtimeProbes.database.checkId,
                    latency_ms: detail.runtimeProbes.database.latencyMs,
                    recommendation: detail.runtimeProbes.database.recommendation,
                    verified_at: detail.runtimeProbes.database.verifiedAt,
                  }
                : null,
              disk: detail.runtimeProbes.disk
                ? {
                    status: detail.runtimeProbes.disk.status,
                    message: detail.runtimeProbes.disk.message,
                    check_id: detail.runtimeProbes.disk.checkId,
                    latency_ms: detail.runtimeProbes.disk.latencyMs,
                    recommendation: detail.runtimeProbes.disk.recommendation,
                    verified_at: detail.runtimeProbes.disk.verifiedAt,
                  }
                : null,
              memory: detail.runtimeProbes.memory
                ? {
                    status: detail.runtimeProbes.memory.status,
                    message: detail.runtimeProbes.memory.message,
                    check_id: detail.runtimeProbes.memory.checkId,
                    latency_ms: detail.runtimeProbes.memory.latencyMs,
                    recommendation: detail.runtimeProbes.memory.recommendation,
                    verified_at: detail.runtimeProbes.memory.verifiedAt,
                  }
                : null,
              docker: detail.runtimeProbes.docker
                ? {
                    status: detail.runtimeProbes.docker.status,
                    message: detail.runtimeProbes.docker.message,
                    check_id: detail.runtimeProbes.docker.checkId,
                    latency_ms: detail.runtimeProbes.docker.latencyMs,
                    recommendation: detail.runtimeProbes.docker.recommendation,
                    verified_at: detail.runtimeProbes.docker.verifiedAt,
                  }
                : null,
              cpu: detail.runtimeProbes.cpu
                ? {
                    status: detail.runtimeProbes.cpu.status,
                    message: detail.runtimeProbes.cpu.message,
                    check_id: detail.runtimeProbes.cpu.checkId,
                    latency_ms: detail.runtimeProbes.cpu.latencyMs,
                    recommendation: detail.runtimeProbes.cpu.recommendation,
                    verified_at: detail.runtimeProbes.cpu.verifiedAt,
                  }
                : null,
            }
          : null,
        verifications: detail.verifications ?? [],
      },
      integrations,
      knowledge,
      dns,
      ssl,
      platformVersion,
      platformVerifications: platformVerifications as never,
      attention: attentionItems.map((item) => ({
        id: item.id,
        severity: item.severity,
        tenant_slug: item.tenantSlug,
        check_id: item.checkId,
        problem: item.problem,
        recommended_action: item.recommendedAction,
        occurred_at: item.occurredAt,
      })),
      doctorMessage,
      lastVerification: attentionItems[0]?.occurredAt ?? null,
    });
  }, [
    detail,
    customer,
    integrations,
    knowledge,
    dns,
    ssl,
    platformVersion,
    platformVerifications,
    attentionItems,
    doctorMessage,
  ]);

  const integrationRows = useMemo(() => deriveIntegrations(integrations), [integrations]);
  const secretRows = secrets ?? [];
  const versionFields = operational?.deployment ?? null;
  const runtimeDomains = operational?.runtimeHealthDomains ?? [];
  const attentionIds = useMemo(() => attentionItems.map((item) => item.id), [attentionItems]);

  const runRemediation = async (incidentId: string, actionId: string) => {
    if (!detail) return;
    const busy = `${incidentId}:${actionId}`;
    setActionBusyKey(busy);
    setActionMessage(null);
    try {
      let result: IncidentActionResult | Awaited<ReturnType<typeof executeIncidentAction>>;
      if (onExecuteAction) {
        result = await onExecuteAction(incidentId, actionId);
      } else {
        result = await executeIncidentAction(incidentId, actionId);
      }
      setActionMessage(result.message);
      if (actionId === "backup.run" || actionId === "run-backup") {
        const verification =
          "verification" in result && result.verification && typeof result.verification === "object"
            ? (result.verification as { detail?: Record<string, unknown> })
            : null;
        const d = verification?.detail ?? {};
        const backupAt = typeof d.backup_at === "string" ? d.backup_at : null;
        const mtime = typeof d.postgres_mtime === "string" ? d.postgres_mtime : null;
        const path =
          typeof d.postgres_sql_path === "string"
            ? d.postgres_sql_path
            : typeof d.backup_archive === "string"
              ? d.backup_archive
              : null;
        const checksum = typeof d.postgres_sha256 === "string" ? d.postgres_sha256 : null;
        const sizeRaw = d.postgres_sql_bytes;
        const sizeBytes =
          typeof sizeRaw === "number"
            ? sizeRaw
            : typeof sizeRaw === "string" && sizeRaw.trim()
              ? Number(sizeRaw)
              : null;
        if (backupAt || (sizeBytes != null && Number.isFinite(sizeBytes) && sizeBytes > 0)) {
          setBackupOverride({
            lastBackup: backupAt,
            lastBackupBytes:
              sizeBytes != null && Number.isFinite(sizeBytes) && sizeBytes > 0 ? sizeBytes : null,
            lastBackupPath: path,
            lastBackupMtime: mtime,
            lastBackupChecksum: checksum,
          });
        }
      }
      await loadVerificationHistory(detail.slug);
      await onActionComplete?.();
    } catch (err) {
      setActionMessage(err instanceof Error ? err.message : "Action failed");
    } finally {
      setActionBusyKey(null);
    }
  };

  const handleRuntimeHealthAction = (input: {
    incidentId: string;
    actionId: string;
  }) => runRemediation(input.incidentId, input.actionId);

  const handleRunBackup = () => {
    if (!detail) return;
    const incidentId = incidentIdForRemediation({
      actionId: "backup.run",
      tenantSlug: detail.slug,
      attentionIds,
    });
    void runRemediation(incidentId, "backup.run");
  };

  const openEvidenceCenter = async (fetchLogs: boolean) => {
    if (!detail) return;
    setEvidenceOpen(true);
    evidenceRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (!fetchLogs) return;
    setLogsLoading(true);
    setLogsError(null);
    try {
      const payload = await fetchTenantLogs(detail.slug);
      setLogLines(payload.lines);
      setLogsFetchedAt(payload.fetched_at);
      setLogsAvailable(payload.available);
      setLogsMessage(payload.message);
      if (!payload.available) {
        setLogsError(payload.message || "Container logs unavailable");
      }
    } catch (err) {
      setLogsError(err instanceof Error ? err.message : "Failed to fetch logs");
      setLogsAvailable(false);
    } finally {
      setLogsLoading(false);
    }
  };

  const evidenceState = operational
    ? assembleEvidenceCenter({
        slug: detail!.slug,
        incidentId: operational.incident?.id ?? null,
        title: operational.incident?.problem ?? `Evidence — ${detail!.displayName}`,
        summary: operational.incident
          ? `${operational.incident.severity}: ${operational.incident.problem}`
          : operational.framing.ops,
        operational,
        logsCommand: `TENANT=${detail!.slug} ./deploy.sh ${detail!.slug} logs`,
        logLines,
        logsFetchedAt,
        logsError,
        logsAvailable,
        logsMessage,
        recommendedAction: operational.recommendedAction,
        rootCause: operational.rootCause,
      })
    : null;

  if (!detail || !operational) return null;

  return (
    <Drawer
      open={Boolean(detail)}
      onClose={onClose}
      title="Tenant Control Center"
      subtitle={`${detail.displayName} · ${detail.slug}`}
      className="drawer--tenant-control"
    >
      {(detail.openAlerts > 0 || customer) ? (
        <div className="drawer-badges">
          {detail.openAlerts > 0 ? (
            <Badge variant="warning">{detail.openAlerts} alert(s)</Badge>
          ) : null}
          {customer ? <Badge variant="accent">{customer.status}</Badge> : null}
        </div>
      ) : null}

      {!detail.hasLiveData ? (
        <p className="drawer-section__copy drawer-section__copy--muted">
          No live stack data — tenant registered but stack not provisioned.
        </p>
      ) : null}
      {customerError ? (
        <p className="drawer-section__copy drawer-section__copy--muted">
          Registry enrichment: {customerError}
        </p>
      ) : null}

      <section className="drawer-section">
        <h3 className="drawer-section__title">Operational truth</h3>
        <OperationalTruthPanel
          state={operational}
          surface="ops"
          onViewDoctorReport={() => {
            doctorReportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          onOpenReadinessItem={(item) => {
            if (item.id === "runtime-doctor") {
              doctorReportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }}
        />
        {operational.incident && onOpenIncident ? (
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginTop: "0.75rem" }}
            onClick={() => onOpenIncident(operational.incident!.id)}
          >
            View Incident
          </button>
        ) : null}
        <button
          type="button"
          className="btn btn-ghost"
          style={{ marginTop: "0.5rem", marginLeft: operational.incident && onOpenIncident ? "0.5rem" : 0 }}
          onClick={() => void openEvidenceCenter(true)}
        >
          View Logs
        </button>
      </section>

      <section className="drawer-section">
        <h3 className="drawer-section__title">Overview</h3>
        <dl className="info-list info-list--compact">
          <div className="info-list__row">
            <dt>Status</dt>
            <dd>{trustValue(customer?.status, LABEL.unknown)}</dd>
          </div>
          <div className="info-list__row">
            <dt>Tenant health</dt>
            <dd>{operational.tenantHealth}</dd>
          </div>
          <div className="info-list__row">
            <dt>Created</dt>
            <dd>{customer?.created_at ? displayValue(customer.created_at) : LABEL.unknown}</dd>
          </div>
          <div className="info-list__row">
            <dt>Host port</dt>
            <dd>
              {detail.stackPort != null
                ? String(detail.stackPort)
                : customer?.api_host_port != null
                  ? String(customer.api_host_port)
                  : "Not allocated"}
            </dd>
          </div>
        </dl>
      </section>

      {evidenceOpen && evidenceState ? (
        <section className="drawer-section" ref={evidenceRef}>
          <h3 className="drawer-section__title">Verification Center</h3>
          <EvidenceCenterPanel
            state={evidenceState}
            focusLogs
            logsLoading={logsLoading}
            onFetchLogs={() => {
              void openEvidenceCenter(true);
            }}
          />
          <div style={{ marginTop: "1rem" }}>
            <VerificationHistoryPanel
              title="Persisted verification history"
              subtitle={`GET /api/verification/history?tenant=${detail.slug} — queryable Verification Event Store.`}
              items={verificationHistory}
              loading={verificationHistoryLoading}
              error={verificationHistoryError}
              emptyMessage="No persisted verification events for this tenant yet."
              onRefresh={() => void loadVerificationHistory(detail.slug)}
            />
          </div>
        </section>
      ) : null}

      <section className="drawer-section">
        <h3 className="drawer-section__title">Verification history</h3>
        <p className="drawer-section__copy drawer-section__copy--muted">
          Persisted VerificationResult events for this tenant — expand only when needed.
        </p>
        <VerificationHistoryPanel
          title="Tenant verification event store"
          subtitle="Every stored VerificationResult for this tenant."
          items={verificationHistory}
          loading={verificationHistoryLoading}
          error={verificationHistoryError}
          emptyMessage="No persisted verification events for this tenant yet."
          onRefresh={() => void loadVerificationHistory(detail.slug)}
          collapseByDefault
        />
      </section>

      <section className="drawer-section">
        <h3 className="drawer-section__title">Integrations</h3>
        {integrations == null ? (
          <p className="drawer-section__copy drawer-section__copy--muted">
            Integration status not loaded.
          </p>
        ) : (
          <ul className="drawer-list">
            {integrationRows.map((row) => (
              <li key={row.id} className="drawer-list__item">
                <span>{row.name}</span>
                <Badge
                  variant={
                    row.status === "Connected"
                      ? "success"
                      : row.status === "Configured"
                        ? "warning"
                        : "neutral"
                  }
                >
                  {row.status}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="drawer-section">
        <h3 className="drawer-section__title">Security</h3>
        {securityHealth ? (
          <dl className="info-list info-list--compact" style={{ marginBottom: "0.75rem" }}>
            <div className="info-list__row">
              <dt>Avg secret age</dt>
              <dd>
                {securityHealth.average_secret_age_days != null
                  ? `${securityHealth.average_secret_age_days}d`
                  : LABEL.notAvailable}
              </dd>
            </div>
            <div className="info-list__row">
              <dt>Needing rotation</dt>
              <dd>{securityHealth.secrets_needing_rotation_count}</dd>
            </div>
            <div className="info-list__row">
              <dt>Failed rotations</dt>
              <dd>{securityHealth.failed_rotations_count}</dd>
            </div>
          </dl>
        ) : null}
        <ul className="drawer-list">
          {secretRows.length === 0 ? (
            <li className="drawer-list__item">
              <span className="drawer-list__meta">
                {secrets === null ? "Inventory unavailable" : "No secrets"}
              </span>
            </li>
          ) : (
            secretRows.map((row) => (
              <li key={row.id} className="drawer-list__item">
                <div>
                  <strong>{row.name}</strong>
                  <div className="drawer-list__meta font-mono">{row.masked || "—"}</div>
                </div>
                <Badge
                  variant={
                    row.status === "healthy"
                      ? "success"
                      : row.status === "old"
                        ? "warning"
                        : "danger"
                  }
                >
                  {row.status === "rotation_recommended"
                    ? "Rotation recommended"
                    : row.status === "compromised"
                      ? "Compromised"
                      : row.status === "missing"
                        ? "Missing"
                        : row.status === "old"
                          ? "Old"
                          : row.status === "healthy"
                            ? "Healthy"
                            : row.status}
                </Badge>
              </li>
            ))
          )}
        </ul>
        <p className="drawer-list__meta" style={{ marginTop: "0.5rem" }}>
          Reveal / Rotate available in Tenant Control Center.
        </p>
      </section>

      <section className="drawer-section" ref={doctorReportRef}>
        <h3 className="drawer-section__title">Deployment</h3>

        <h4 className="drawer-section__subtitle">Platform</h4>
        <dl className="info-list info-list--compact">
          <div className="info-list__row">
            <dt>Platform version</dt>
            <dd>{versionFields?.platformVersion ?? LABEL.notAvailable}</dd>
          </div>
          <div className="info-list__row">
            <dt>Deployment environment</dt>
            <dd>{versionFields?.deploymentEnvironment ?? LABEL.notAvailable}</dd>
          </div>
          <div className="info-list__row">
            <dt>Git commit</dt>
            <dd>{versionFields?.gitCommit ?? LABEL.notAvailable}</dd>
          </div>
          <div className="info-list__row">
            <dt>Branch</dt>
            <dd>{versionFields?.branch ?? LABEL.notAvailable}</dd>
          </div>
        </dl>

        <h4 className="drawer-section__subtitle">Deployment</h4>
        <dl className="info-list info-list--compact">
          <div className="info-list__row">
            <dt>Docker image</dt>
            <dd>{versionFields?.dockerImage ?? LABEL.notAvailable}</dd>
          </div>
          <div className="info-list__row">
            <dt>Image tag</dt>
            <dd>{versionFields?.imageTag ?? LABEL.notAvailable}</dd>
          </div>
          <div className="info-list__row">
            <dt>Build time</dt>
            <dd>{formatDeployTimestamp(versionFields?.buildTime)}</dd>
          </div>
          <div className="info-list__row">
            <dt>Deployment time</dt>
            <dd>{formatDeployTimestamp(versionFields?.deploymentTime)}</dd>
          </div>
        </dl>

        <h4 className="drawer-section__subtitle">Health</h4>
        <div className="drawer-health-block">
          <div className="drawer-doctor-row">
            <span className="drawer-list__meta">Runtime Doctor</span>
            <RuntimeDoctorStatus status={detail.doctorStatus} />
          </div>
          <p className="drawer-section__copy drawer-section__copy--muted">
            Platform health, reason, and evidence are shown in Operational truth above
            (shared state — no second derivation).
          </p>
        </div>
      </section>

      <section className="drawer-section">
        <h3 className="drawer-section__title">Runtime Health</h3>
        <p className="drawer-section__copy drawer-section__copy--muted">
          Domain summaries — expand a domain for component cards, then Details for diagnostics.
          Unhealthy components expose executable remediation when the platform supports it.
        </p>
        {actionMessage ? (
          <p className="drawer-section__copy" role="status">
            {actionMessage}
          </p>
        ) : null}
        <RuntimeHealthDomainsPanel
          domains={runtimeDomains}
          surface="ops"
          tenantSlug={detail.slug}
          attentionIds={attentionIds}
          actionBusyKey={actionBusyKey}
          onExecuteAction={(input) => void handleRuntimeHealthAction(input)}
        />
      </section>

      <section className="drawer-section">
        <h3 className="drawer-section__title">Backup</h3>
        <p className="drawer-section__copy drawer-section__copy--muted">
          Metadata from the verified backup artifact on disk.
        </p>
        <dl className="info-list info-list--compact">
          <div className="info-list__row">
            <dt>Last backup</dt>
            <dd>
              {formatBackupTime(
                backupOverride?.lastBackupMtime ??
                  backupOverride?.lastBackup ??
                  detail.lastBackupMtime ??
                  detail.lastBackup,
              )}
            </dd>
          </div>
          <div className="info-list__row">
            <dt>Status</dt>
            <dd>
              {backupStatusLabel(
                backupOverride?.lastBackup ?? detail.lastBackup,
                detail.hasLiveData,
              )}
            </dd>
          </div>
          <div className="info-list__row">
            <dt>Size</dt>
            <dd>
              {formatBackupSize(
                backupOverride?.lastBackupBytes ?? detail.lastBackupBytes,
              ) ?? LABEL.notAvailable}
            </dd>
          </div>
          <div className="info-list__row">
            <dt>File path</dt>
            <dd>
              {backupOverride?.lastBackupPath ?? detail.lastBackupPath
                ? (backupOverride?.lastBackupPath ?? detail.lastBackupPath)
                : LABEL.notAvailable}
            </dd>
          </div>
          <div className="info-list__row">
            <dt>Checksum</dt>
            <dd>
              {(backupOverride?.lastBackupChecksum ?? detail.lastBackupChecksum)
                ? `${(backupOverride?.lastBackupChecksum ?? detail.lastBackupChecksum)!.slice(0, 16)}…`
                : LABEL.notAvailable}
            </dd>
          </div>
        </dl>
        <button
          type="button"
          className="btn btn-primary"
          style={{ marginTop: "0.65rem" }}
          disabled={Boolean(actionBusyKey)}
          onClick={() => void handleRunBackup()}
        >
          {actionBusyKey?.endsWith(":backup.run") || actionBusyKey?.endsWith(":run-backup")
            ? "Running backup…"
            : "Run Backup"}
        </button>
      </section>

      <section className="drawer-section">
        <h3 className="drawer-section__title">Audit</h3>
        {detail.recentEvents.length === 0 ? (
          <p className="drawer-section__copy drawer-section__copy--muted">
            No audit events for this tenant.
          </p>
        ) : (
          <ul className="drawer-list">
            {detail.recentEvents.map((event) => (
              <li key={`${event.title}-${event.at}`} className="drawer-list__item">
                <div>
                  <strong>{event.title}</strong>
                  <div className="drawer-list__meta">{event.at}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </Drawer>
  );
}

/** @deprecated use TenantControlCenterDrawer */
export const TenantDetailsDrawer = TenantControlCenterDrawer;
